import { useCallback, type FormEvent, useEffect, useMemo, useState } from 'react'
import { ExternalLink, Save } from 'lucide-react'
import { adminApi } from '../api'
import { Btn, Card, Field, Page, SettingsSkeleton, inputClass } from '../ui'

type Settings = Record<string, string | null>

type Social = {
  id: number
  platform: string
  url: string
  is_active: number
  sort_order: number
}

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/…' },
  { value: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/…' },
  { value: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/…' },
  { value: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@…' },
  { value: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/…' },
] as const

type PlatformKey = (typeof PLATFORMS)[number]['value']

type Slot = {
  platform: PlatformKey
  id: number | null
  url: string
  is_active: boolean
  dirty: boolean
  saving: boolean
}

const FIELD_GROUPS: {
  title: string
  blurb: string
  fields: { key: string; label: string; hint?: string; type?: string; full?: boolean }[]
}[] = [
  {
    title: 'Brand',
    blurb: 'Shown in SEO text and site chrome where wired.',
    fields: [
      { key: 'site_name', label: 'Site name' },
      { key: 'tagline', label: 'Tagline', full: true, hint: 'Short line under the logo in the footer.' },
    ],
  },
  {
    title: 'Contact',
    blurb: 'Used on Contact page, footer, and WhatsApp float.',
    fields: [
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone', hint: 'Display format, e.g. +91 8200670752' },
      {
        key: 'whatsapp',
        label: 'WhatsApp',
        hint: 'Digits with country code, e.g. 918200670752 (no + or spaces).',
      },
    ],
  },
  {
    title: 'Address',
    blurb: 'Contact page + footer address block.',
    fields: [
      { key: 'address_line1', label: 'Address line 1', full: true },
      { key: 'address_line2', label: 'Address line 2', full: true },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'postal_code', label: 'Postal code' },
      { key: 'country', label: 'Country' },
    ],
  },
  {
    title: 'Map',
    blurb: 'Google Maps embed on the Contact page.',
    fields: [
      {
        key: 'map_url',
        label: 'Map embed URL',
        full: true,
        hint: 'Paste the iframe src from Google Maps → Share → Embed a map.',
      },
    ],
  },
]

function buildSlots(list: Social[]): Slot[] {
  return PLATFORMS.map((p) => {
    const found = list.find((s) => String(s.platform || '').toLowerCase() === p.value)
    return {
      platform: p.value,
      id: found?.id ?? null,
      url: found?.url || '',
      is_active: found ? !!found.is_active : true,
      dirty: false,
      saving: false,
    }
  })
}

function Toggle({
  on,
  disabled,
  onChange,
  label,
}: {
  on: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
        on ? 'bg-emerald-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
          on ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

export default function SettingsPage() {
  const [settings, setSettings] = useState<Settings>({})
  const [slots, setSlots] = useState<Slot[]>(() => buildSlots([]))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [socialMsg, setSocialMsg] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await adminApi<{ settings?: Settings; socialLinks?: Social[] }>(
      '/api/admin/website/settings'
    )
    setSettings(data.settings || {})
    setSlots(buildSlots(data.socialLinks || []))
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const platformMeta = useMemo(
    () => Object.fromEntries(PLATFORMS.map((p) => [p.value, p])) as Record<
      PlatformKey,
      (typeof PLATFORMS)[number]
    >,
    []
  )

  function patchSlot(platform: PlatformKey, patch: Partial<Slot>) {
    setSlots((prev) =>
      prev.map((s) => (s.platform === platform ? { ...s, ...patch, dirty: true } : s))
    )
    setSocialMsg(null)
  }

  async function saveSlot(slot: Slot) {
    const url = slot.url.trim()
    if (!url) {
      // Empty URL → delete if exists
      if (slot.id) {
        setSlots((prev) =>
          prev.map((s) => (s.platform === slot.platform ? { ...s, saving: true } : s))
        )
        await adminApi(`/api/admin/website/social/${slot.id}`, { method: 'DELETE' })
        setSlots((prev) =>
          prev.map((s) =>
            s.platform === slot.platform
              ? { ...s, id: null, url: '', is_active: true, dirty: false, saving: false }
              : s
          )
        )
        setSocialMsg(`${platformMeta[slot.platform].label} cleared`)
      }
      return
    }

    setSlots((prev) =>
      prev.map((s) => (s.platform === slot.platform ? { ...s, saving: true } : s))
    )
    const sort_order = PLATFORMS.findIndex((p) => p.value === slot.platform) + 1
    const body = {
      platform: slot.platform,
      url,
      is_active: slot.is_active ? 1 : 0,
      sort_order,
    }

    if (slot.id) {
      const { res } = await adminApi(`/api/admin/website/social/${slot.id}`, {
        method: 'PUT',
        json: body,
      })
      setSlots((prev) =>
        prev.map((s) =>
          s.platform === slot.platform
            ? { ...s, url, saving: false, dirty: !res.ok }
            : s
        )
      )
      setSocialMsg(res.ok ? `${platformMeta[slot.platform].label} saved` : 'Save failed')
      return
    }

    const { res, data } = await adminApi<{ id?: number }>('/api/admin/website/social', {
      method: 'POST',
      json: body,
    })
    if (res.ok && data.id) {
      setSlots((prev) =>
        prev.map((s) =>
          s.platform === slot.platform
            ? { ...s, id: data.id!, url, saving: false, dirty: false }
            : s
        )
      )
      setSocialMsg(`${platformMeta[slot.platform].label} saved`)
    } else {
      setSlots((prev) =>
        prev.map((s) => (s.platform === slot.platform ? { ...s, saving: false } : s))
      )
      setSocialMsg('Save failed')
    }
  }

  async function toggleActive(slot: Slot, next: boolean) {
    setSlots((prev) =>
      prev.map((s) => (s.platform === slot.platform ? { ...s, is_active: next } : s))
    )
    if (!slot.id) {
      setSlots((prev) =>
        prev.map((s) =>
          s.platform === slot.platform ? { ...s, is_active: next, dirty: true } : s
        )
      )
      return
    }
    const { res } = await adminApi(`/api/admin/website/social/${slot.id}`, {
      method: 'PUT',
      json: {
        platform: slot.platform,
        url: slot.url,
        is_active: next ? 1 : 0,
        sort_order: PLATFORMS.findIndex((p) => p.value === slot.platform) + 1,
      },
    })
    if (res.ok) {
      setSocialMsg(
        next
          ? `${platformMeta[slot.platform].label} On`
          : `${platformMeta[slot.platform].label} Off`
      )
    } else {
      setSlots((prev) =>
        prev.map((s) => (s.platform === slot.platform ? { ...s, is_active: !next } : s))
      )
      setSocialMsg('Toggle failed')
    }
  }

  async function saveSettings(e: FormEvent) {
    e.preventDefault()
    setMsg(null)
    setErr(null)
    setSaving(true)
    const body: Record<string, string | null> = {}
    for (const g of FIELD_GROUPS) {
      for (const f of g.fields) body[f.key] = (settings[f.key] ?? '').toString().trim() || null
    }
    const { res, data } = await adminApi<{ message?: string }>('/api/admin/website/settings', {
      method: 'PUT',
      json: body,
    })
    setSaving(false)
    if (!res.ok) {
      setErr(data.message || 'Save failed')
      return
    }
    setMsg('Settings saved — live on Contact, footer & WhatsApp')
  }

  if (loading) {
    return (
      <Page>
        <SettingsSkeleton />
      </Page>
    )
  }

  return (
    <Page
      actions={
        <a
          href="/contact"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-xl border border-primary/15 bg-white px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/[0.04]"
        >
          View contact <ExternalLink className="h-3.5 w-3.5" />
        </a>
      }
    >
      <form onSubmit={saveSettings} className="space-y-4">
        {FIELD_GROUPS.map((group) => (
          <Card key={group.title} className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-navy-900">{group.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{group.blurb}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {group.fields.map((f) => (
                <div key={f.key} className={f.full ? 'sm:col-span-2' : undefined}>
                  <Field label={f.label}>
                    <input
                      className={inputClass}
                      type={f.type || 'text'}
                      value={settings[f.key] || ''}
                      onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                      placeholder={f.label}
                    />
                  </Field>
                  {f.hint && <p className="mt-1 text-[0.7rem] text-slate-500 leading-relaxed">{f.hint}</p>}
                </div>
              ))}
            </div>
          </Card>
        ))}

        <div className="flex flex-wrap items-center gap-3">
          <Btn type="submit" disabled={saving}>
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save settings'}
          </Btn>
          {msg && <span className="text-sm text-emerald-700">{msg}</span>}
          {err && <span className="text-sm text-red-600">{err}</span>}
        </div>
      </form>

      <Card className="space-y-3 mt-2">
        <div>
          <p className="text-sm font-semibold text-navy-900">Social links</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Paste URL → Save. Toggle On/Off for footer. Clear URL + Save to remove.
          </p>
        </div>

        <ul className="space-y-2">
          {slots.map((slot) => {
            const meta = platformMeta[slot.platform]
            return (
              <li
                key={slot.platform}
                className="flex flex-col gap-2 rounded-xl border border-primary/10 bg-slate-50/50 p-3 sm:flex-row sm:items-center"
              >
                <span className="w-28 shrink-0 text-sm font-semibold text-navy-900">{meta.label}</span>
                <input
                  className={`${inputClass} min-w-0 flex-1`}
                  type="url"
                  value={slot.url}
                  onChange={(e) => patchSlot(slot.platform, { url: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void saveSlot(slot)
                    }
                  }}
                  placeholder={meta.placeholder}
                />
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[0.7rem] font-semibold uppercase text-slate-500 w-8">
                    {slot.is_active ? 'On' : 'Off'}
                  </span>
                  <Toggle
                    on={slot.is_active}
                    disabled={slot.saving || !slot.url.trim()}
                    label={slot.is_active ? 'Turn off' : 'Turn on'}
                    onChange={(next) => void toggleActive(slot, next)}
                  />
                  <Btn
                    disabled={slot.saving || (!slot.dirty && !!slot.id)}
                    onClick={() => void saveSlot({ ...slot, url: slot.url })}
                  >
                    <Save className="h-4 w-4" />
                    {slot.saving ? '…' : 'Save'}
                  </Btn>
                </div>
              </li>
            )
          })}
        </ul>
        {socialMsg && <p className="text-sm text-emerald-700">{socialMsg}</p>}
      </Card>
    </Page>
  )
}
